import { useState } from "react";
import MarketingSummary from "../../components/MarketingSummary";
import MarketingForm from "../../components/MarketingForm";
import LeadCaptureModal from "../../components/LeadCaptureModal";
import { runMarketingDiagnostics } from "../../api/diagnosticsApi";
import { Loader2, Zap, CheckCircle2, Mail } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";

export default function MarketingTool() {
  const [report, setReport] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [requiresLeadCapture, setRequiresLeadCapture] = useState(false);
  const [pendingAnswers, setPendingAnswers] = useState<Record<string, string>>({});
  const [leadCaptureId, setLeadCaptureId] = useState<string | null>(null);
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [capturedEmail, setCapturedEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  
  const { token } = useAuth();
  const isAuthenticated = !!token;
  const pageWrapperClass = isAuthenticated
  ? "min-h-screen pt-10 p-6 md:p-8 lg:p-10 md:ml-0"
  : "min-h-screen px-4 sm:px-6 lg:px-8 text-slate-100";

  async function runDiagnostic(answers: Record<string, string>) {
    setIsLoading(true);
    setError(null);
    setPendingAnswers(answers);
    try {
      const data = await runMarketingDiagnostics({ answers });
      if (data.requiresLeadCapture) {
        setRequiresLeadCapture(true);
        setReport(null);
        setLeadCaptured(false);
      } else {
        setReport(data.report ?? data);
        setRequiresLeadCapture(false);
        setLeadCaptured(false);
        setLeadCaptureId(data.leadCaptureId ?? null);
      }
    } catch (err) {
      console.error("Error running marketing diagnostic:", err);
      setError(err instanceof Error ? err.message : "Failed to run diagnostics. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }

  async function handleLeadCaptureSubmit(data: { name: string; company: string; email: string }) {
    setIsLoading(true);
    try {
      const response = await runMarketingDiagnostics({ answers: pendingAnswers, ...data });
      if (response.report) {
        setReport(response.report);
        setRequiresLeadCapture(false);
        setLeadCaptured(true);
        setLeadCaptureId(response.leadCaptureId ?? null);
        setCapturedEmail(data.email);
      } else {
        throw new Error("No report was returned after lead capture.");
      }
    } catch (err) {
      console.error("Error submitting lead capture:", err);
      setError(err instanceof Error ? err.message : "Failed to submit lead capture. Please try again.");
      throw err;
    } finally {
      setIsLoading(false);
    }
  }

  function resetState() {
    setRequiresLeadCapture(false);
    setLeadCaptured(false);
    setCapturedEmail(null);
    setLeadCaptureId(null);
    setReport(null);
    setError(null);
    setPendingAnswers({});
  }

  return (
    <div className={pageWrapperClass}>
      <LeadCaptureModal
        isOpen={requiresLeadCapture}
        onClose={resetState}
        onSubmit={handleLeadCaptureSubmit}
        loading={isLoading}
        websiteUrl="your marketing diagnostic"
        title="Get Your Marketing Report"
        description="We need a few details so we can send your full report and follow up with tailored recommendations."
        companyLabel="Company"
      />
      
      {!isAuthenticated ? (
        <header className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen bg-slate-950 text-white border-b border-slate-800 shadow-lg mb-12">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">

            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center">
                <span className="text-sky-400 font-bold">L</span>
              </div>

              <h1 className="font-bold text-xl tracking-tight bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">
                Strivre Diagnostics
              </h1>
            </div>

            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <Link
                to="/diagnostics/website-tool"
                className="text-slate-300 hover:text-sky-400 transition-colors"
              >
                Web Diagnostics
              </Link>

              <Link
                to="/diagnostics/marketing-tool"
                className="text-sky-400 font-medium"
              >
                Marketing Diagnostics
              </Link>

              <Link
                to="/diagnostics/business-assessment"
                className="text-slate-300 hover:text-sky-400 transition-colors"
              >
                Business Diagnostics
              </Link>
            </nav>

            <div className="flex items-center gap-3">
                <button
                  className="btn-primary btn-md shrink-0"
                  onClick={() => navigate("/login")}
                >
                  Login
                </button>

                <button
                  className="btn-secondary btn-md shrink-0"
                  onClick={() => navigate("/register")}
                >
                  Sign Up
                </button>
            </div>
          </div>
        </header>
      ) : (
        <header className="mt-5 mb-8 flex items-center justify-between animate-fade-in">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2 leading-tight mb-0">
              <Zap className="text-indigo-600" size={24} />
              {report && !isLoading
                ? "Diagnostic Results"
                : "Marketing Diagnostics"}
            </h1>

            <p
              className={`text-slate-500 text-sm mt-1 ${
                !report || isLoading ? "text-center" : "text-left"
              }`}
            >
              {report && !isLoading
                ? "Review your personalized marketing maturity and CRM health analysis below."
                : "Answer a few questions to evaluate your marketing maturity and CRM health."}
            </p>
          </div>
        </header>
      )}

      {!report && !isLoading && !requiresLeadCapture && (
        <div className="w-full">
          {error && (
            <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {error}
            </div>
          )}
          <MarketingForm onSubmit={runDiagnostic} />
        </div>
      )}

      {report && !isLoading && !requiresLeadCapture && (
        <>
          {!isAuthenticated && leadCaptured ? (
            <div className="max-w-2xl mx-auto mb-12 animate-fade-in">
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-3xl border-2 border-emerald-200 p-12 text-center shadow-lg">
                
                <div className="mb-6 flex justify-center">
                  <div className="relative">
                    <div className="w-20 h-20 bg-emerald-600 rounded-full flex items-center justify-center">
                      <CheckCircle2 size={40} className="text-white" />
                    </div>

                    <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center border-4 border-white">
                      <Mail size={16} className="text-white" />
                    </div>
                  </div>
                </div>

                <h2 className="text-3xl font-bold text-slate-900 mb-3">
                  Report Sent to Your Email!
                </h2>

                <p className="text-slate-700 text-lg mb-2">
                  We've sent your detailed marketing report to:
                </p>

                <p className="text-emerald-700 font-semibold text-lg mb-8 break-all">
                  {capturedEmail}
                </p>

                <div className="space-y-3 text-left text-slate-700 mb-8 max-w-md mx-auto">

                  <div className="flex items-start gap-3">
                    <CheckCircle2
                      size={20}
                      className="text-emerald-600 flex-shrink-0 mt-1"
                    />
                    <p>
                      Check your inbox for your complete marketing diagnostic report
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle2
                      size={20}
                      className="text-emerald-600 flex-shrink-0 mt-1"
                    />
                    <p>
                      Your report contains detailed recommendations and action plans
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle2
                      size={20}
                      className="text-emerald-600 flex-shrink-0 mt-1"
                    />
                    <p>
                      Our team may reach out to discuss opportunities for growth
                    </p>
                  </div>

                </div>

                <p className="text-sm text-slate-600 mb-6">
                  Didn't receive the email? Check your spam folder or{" "}
                  <button
                    onClick={resetState}
                    className="text-emerald-700 hover:text-emerald-800 font-semibold underline"
                  >
                    try another email
                  </button>
                </p>

                <button
                  onClick={resetState}
                  className="inline-block px-8 py-3 bg-emerald-600 text-white font-bold rounded-full hover:bg-emerald-700 transition-colors"
                >
                  Run Another Diagnostic
                </button>

              </div>
            </div>
          ) : (
            <div className="mt-8">
              <MarketingSummary
                overallPercentage={report.overallPercentage}
                visualIndicator={report.visualIndicator || report.grade}
                categoryScores={report.categoryScores}
                report={report.summaryReport}
                recommendations={report.recommendations}
                services={report.ctaServices}
              />
            </div>
          )}
        </>
      )}

      {isLoading && (
        <div className="flex flex-col items-center justify-center py-20 animate-pulse">
          <Loader2 className="w-12 h-12 text-pink-600 animate-spin mb-4" />
          <p className="text-slate-500 font-medium text-lg">Analyzing your marketing engine...</p>
        </div>
      )}

      {report && !isLoading && !requiresLeadCapture && (
        <div className="mt-8">
          {leadCaptured && capturedEmail && (
            <div className="mb-6 rounded-3xl border border-emerald-200 bg-emerald-50 p-6 text-left shadow-sm">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="text-emerald-600" size={24} />
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Your report is ready</h2>
                  <p className="text-sm text-slate-600">We sent a copy to {capturedEmail} and stored your lead reference #{leadCaptureId || 'n/a'}.</p>
                </div>
              </div>
            </div>
          )}
          <MarketingSummary
            overallPercentage={report.overallPercentage}
            visualIndicator={report.visualIndicator || report.grade}
            categoryScores={report.categoryScores}
            report={report.summaryReport}
            recommendations={report.recommendations}
            services={report.ctaServices}
          />
        </div>
      )}
      {!isAuthenticated && (
        <footer className="fixed bottom-0 left-0 w-screen bg-slate-950 border-t border-slate-800 py-4 z-40">
          <div className="max-w-7xl mx-auto px-6 text-center text-sm text-slate-500">
            © 2026 Strivre. All rights reserved.
          </div>
        </footer>
      )}
    </div>
  );
}
